import { gql } from "@apollo/client";
import { Building2Icon, BuildingIcon, GlobeIcon } from "lucide-react";

import { BrandsQuery, OrganizationFragment } from "@/__generated__/graphql";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { BoxForOrganization } from "@/components/atoms/BoxForOrganization";
import { Mutations, Queries } from "@/constants";
import { Values } from "@/constants/values";
import { icons } from "@/objects/@icons";
import { ObjectCardProps, ObjectType } from "@/objects/@types";
import { getReferenceValueOptionsByTypename } from "@/objects";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";

/** A brand Brandfetch knows, as an organization picker lists it: known by its domain. */
type BrandOption = NonNullable<BrandsQuery["brands"]["data"]>[number] & {
  identifier: string;
};

/** The brand drawn while Brandfetch is asked: a typical one, each piece wrapped. */
const PLACEHOLDER_BRAND: BrandOption = {
  __typename: "BrandObject",
  identifier: "placeholder",
  name: "Northwind Supply",
  domain: "northwind.example",
  logoUrl: null,
};

/** An organization as a picker lists it: its logo, its name, and its domain under it. */
function OrganizationOptionRow({
  organization,
  isLoading = false,
}: {
  organization: Pick<OrganizationFragment, "name" | "domain" | "logoUrl">;
  isLoading?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Skeleton
        loading={isLoading}
        variant="rectangular"
        className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
      >
        <BoxForOrganization organization={organization} size="md" />
      </Skeleton>
      <div className="min-w-0 flex-1">
        <Typography level="body-sm" className="truncate">
          <Skeleton loading={isLoading} variant="inline">
            {organization.name}
          </Skeleton>
        </Typography>
        {organization.domain && (
          <Typography
            level="body-xs"
            className="truncate text-muted-foreground/80"
          >
            <Skeleton loading={isLoading} variant="inline">
              {organization.domain}
            </Skeleton>
          </Typography>
        )}
      </div>
    </div>
  );
}

function OrganizationCard({
  data,
  isLoading = false,
}: ObjectCardProps<OrganizationFragment>) {
  return data?.domain ? (
    <Typography level="body-sm" className="truncate text-muted-foreground">
      <Skeleton loading={isLoading} variant="inline">
        {data.domain}
      </Skeleton>
    </Typography>
  ) : null;
}

const object: ObjectType<OrganizationFragment> = {
  humanName: "organization",
  inputKey: "organization",
  typename: "OrganizationObject",
  ReactIcon: Building2Icon,
  isEntity: false,
  titleFieldKey: "name",
  Card: OrganizationCard,
  placeholder: {
    __typename: "OrganizationObject",
    id: btoa("OrganizationObject:placeholder"),
    identifier: "placeholder",
    name: "Northwind Supply",
    domain: "northwind.example",
    logoUrl: null,
    entity: {
      id: btoa("EntityObject:placeholder"),
      identifier: "placeholder",
      name: "Elm Street Coffee",
    },
  } as OrganizationFragment,
  getTitle: (data) => {
    return data?.name ?? "";
  },
  getEntityId: (data) => {
    return data?.entity?.id;
  },
  getOrganization: (data) => data,
  // The entity's own organizations, and - for a field - Brandfetch's brands
  // for what is typed, under them: picking a brand makes it one of the
  // entity's. A filter looks for the organizations there are.
  getReferenceValueOptions: (entityIdentifiers, purpose = "value") => {
    const entityIdentifier = entityIdentifiers?.[0] ?? null;
    return {
      query: gql`
        query OrganizationList($entityIdentifiers: [String!]) {
          organizations(entityIdentifiers: $entityIdentifiers) {
            data {
              ...Organization
            }
          }
        }
      `,
      queryOptions: {
        variables: { entityIdentifiers },
      },
      onRenderOption: (option: OrganizationFragment | BrandOption) => (
        <OrganizationOptionRow
          organization={option}
          isLoading={option === PLACEHOLDER_BRAND}
        />
      ),
      filterOptions: (
        options: (OrganizationFragment | BrandOption)[],
        filter: string,
      ) => {
        const needle = filter.toLowerCase();
        // A brand is Brandfetch's answer to what is typed already.
        return options.filter(
          (option) =>
            option.__typename === "BrandObject" ||
            option.name?.toLowerCase().includes(needle) ||
            option.domain?.toLowerCase().includes(needle),
        );
      },
      getOptionGroup: (option: OrganizationFragment | BrandOption) =>
        option.__typename === "BrandObject"
          ? { key: "brands", label: "From Brandfetch" }
          : null,
      // Searched live as the person types and never kept, as Brandfetch's
      // terms ask.
      searchOptions:
        purpose === "value"
          ? {
              query: Queries.BRANDS_QUERY,
              variables: (filter: string) =>
                filter.length >= 2 && entityIdentifier
                  ? { query: filter }
                  : null,
              prepareOptions: (data: BrandsQuery) =>
                (data?.brands?.data ?? []).map(
                  (brand): BrandOption => ({
                    ...brand,
                    identifier: brand.domain,
                  }),
                ),
              placeholder: PLACEHOLDER_BRAND,
            }
          : undefined,
      // A picked brand is made the entity's organization - its own of that
      // domain, where it has one already - and that goes on the field.
      resolveSelection: async (
        option: OrganizationFragment | BrandOption,
        { apolloClient },
      ) => {
        if (option.__typename !== "BrandObject") {
          return option;
        }
        const { data } = await apolloClient.mutate({
          mutation: Mutations.CREATE_NODE,
          variables: {
            input: {
              entityIdentifier,
              node: {
                organization: { name: option.name, domain: option.domain },
              },
            },
          },
          refetchQueries: ["OrganizationList"],
        });
        return (
          (data?.createNode?.data?.[0] as OrganizationFragment | undefined) ??
          null
        );
      },
      mutationOptions: {
        createNewLabel: "Create new organization",
        createTypename: "OrganizationObject",
        entityIdentifier,
      },
    };
  },
  fieldDefinitions: [
    {
      objectKey: "entity",
      inputKey: "entity",
      label: "",
      type: "custom",
      ReactIcon: BuildingIcon,
      asString: (data) => data?.entity?.identifier,
      getValue: (data) => () => (
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <BoxForEntity
            identifier={data?.entity?.identifier}
            representation="character"
          />
        </div>
      ),
      fieldProps: {
        exportPath: "entity",
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: false,
        isLockedLeft: true,
        isResizable: false,
        minWidth: Values.DEFAULT_ROW_HEIGHT,
        width: Values.DEFAULT_ROW_HEIGHT,
        maxWidth: Values.DEFAULT_ROW_HEIGHT,
      },
    },
    {
      objectKey: "self",
      inputKey: "self",
      label: "Organization",
      type: "reference",
      ReactIcon: Building2Icon,
      asString: (data) => data?.identifier,
      getValue: (data) => ({
        __typename: "OrganizationObject",
        name: data?.name,
        id: data?.id,
        identifier: data?.identifier,
        entityId: data?.entity?.id,
        logoUrl: data?.logoUrl,
      }),
      getValueOptions: (data) => ({
        isNullable: false,
        ...getReferenceValueOptionsByTypename("OrganizationObject")([
          data?.entity?.identifier,
        ]),
        style: "self",
      }),
      fieldProps: {
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: true,
        isLockedLeft: true,
        isResizable: true,
      },
    },
    {
      objectKey: "name",
      label: "Name",
      type: "text",
      ReactIcon: icons.name,
      asString: (data) => data?.name ?? "",
      getValue: (data) => data?.name,
      createProps: {
        isRequired: true,
        isEditable: true,
      },
      updateProps: {
        isRequired: true,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      // Its website's domain - its logo is drawn from it. One a person makes
      // is made with it, as the server asks; one made without - from a
      // receipt's sender, say - is saved as it is, as the server keeps it.
      objectKey: "domain",
      label: "Domain",
      type: "text",
      ReactIcon: GlobeIcon,
      asString: (data) => data?.domain ?? "",
      getValue: (data) => data?.domain,
      getValueOptions: () => ({
        placeholder: "amazon.ca, rbc.com, etc.",
      }),
      createProps: {
        isRequired: true,
        isEditable: true,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
  ],
};

export { object as OrganizationObject };
